import type { HeightmapAsset, PanelDesign } from '../types';
import { normalizeDesign, type NormalizeResult } from './validate';

/**
 * Design files.
 *
 * A `.organic3d.json` file is a plain JSON document with the bitmaps embedded,
 * so a design is self-contained and can be shared or checked into a repository.
 *
 * Height samples are stored as 8-bit values. Imported bitmaps are 8-bit to
 * begin with, so this is lossless for the common case while keeping a 1024²
 * heightmap around 1.4 MB of base64 instead of 5.5 MB of JSON numbers.
 */

export const DESIGN_FILE_EXTENSION = '.organic3d.json';

interface SerializedAsset {
  id: string;
  name: string;
  width: number;
  height: number;
  encoding: 'base64-u8';
  data: string;
}

function encodeBase64(bytes: Uint8Array): string {
  let binary = '';
  // btoa takes a string; chunking avoids blowing the argument limit on
  // String.fromCharCode for large bitmaps.
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function encodeAsset(asset: HeightmapAsset): SerializedAsset {
  const bytes = new Uint8Array(asset.data.length);
  for (let i = 0; i < asset.data.length; i += 1) {
    bytes[i] = Math.round(Math.min(1, Math.max(0, asset.data[i])) * 255);
  }
  return {
    id: asset.id,
    name: asset.name,
    width: asset.width,
    height: asset.height,
    encoding: 'base64-u8',
    data: encodeBase64(bytes),
  };
}

function decodeAsset(raw: SerializedAsset): HeightmapAsset {
  const bytes = decodeBase64(raw.data);
  const data = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) data[i] = bytes[i] / 255;
  return { id: raw.id, name: raw.name, width: raw.width, height: raw.height, data };
}

export function serializeDesign(design: PanelDesign): string {
  return JSON.stringify({ ...design, assets: design.assets.map(encodeAsset) }, null, 2);
}

export function parseDesign(json: string): NormalizeResult {
  const raw = JSON.parse(json) as Record<string, unknown>;

  if (Array.isArray(raw.assets)) {
    raw.assets = (raw.assets as SerializedAsset[]).map((asset) =>
      typeof asset?.data === 'string' ? decodeAsset(asset) : asset,
    );
  }

  return normalizeDesign(raw);
}

export function designFilename(design: PanelDesign): string {
  const slug = design.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'design'}${DESIGN_FILE_EXTENSION}`;
}
